import React, { useMemo, useState } from 'react';
import { Button, Modal, Select, Space, Table, Typography } from 'antd';
import { InfoCircleOutlined, FolderAddOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import NumberInput from '../../common/NumberInput';
import { useDevMode } from '@/contexts/DevModeContext';
import { useTender } from '../../../contexts/TenderContext';
import { formatUnit } from '../../../utils/constants';
import { buildStormBuildUpSummaryData, type StormSummaryRow } from '../../../utils/stormSummaryUtils';
import type { StormBuildUpRowDef } from '../../../utils/stormBuildUpTypes';
import type { FoulBuildUpRowData, DynamicBillSummaryNode, DynamicBillSummaryItemNode, DynamicBillSummarySectionNode } from '../../../types/tender.types';
import type { BoqSummaryLineItem } from '../../../utils/groundworksBoqSummaryComputer';
import { BcCalculationDetails, computeBcCalculation } from '../../common/BcCalculationDetails';
import { buildRootExtraRows, buildNestedExtraRows, type BillSummaryExtraRow, type useBillSummaryExtraItems } from '../../../hooks/useBillSummaryExtraItems';

const { Text } = Typography;

const fmt = (n: number) =>
  n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtCurrency = (n: number) =>
  `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// A few evaluate codes are combined (e.g. "ALPE010+L001B") — sum whichever parts
// resolve to a library rate, same convention as FoulBuildUpBillSummary.tsx's own getLibRate.
const getLibRate = (rateMap: Record<string, number>, code?: string): number | undefined => {
  if (!code) return undefined;
  if (code.includes('+')) {
    const parts = code.split('+').map((c) => rateMap[c.trim()]).filter((r): r is number => r !== undefined);
    return parts.length > 0 ? parts.reduce((sum, r) => sum + r, 0) : undefined;
  }
  return rateMap[code.trim()];
};

const findExtraNode = (nodes: DynamicBillSummaryNode[], id: string): DynamicBillSummaryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.type === 'section') {
      const found = findExtraNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

// Ad-hoc extra rows (useBillSummaryExtraItems.tsx), same "generic extra row →
// this table's own row shape" adapter convention FoulBuildUpBillSummary.tsx
// uses. Extra items can attach at the root ("__root__" bucket) or graft
// directly onto any real pipe-type row (that row's own `key` as bucket).
// Extended locally since these fields only ever appear on synthetic rows this
// component builds itself.
type ExtStormRow = StormSummaryRow & {
  isExtra?: boolean;
  nodeType?: 'section' | 'item';
  amountType?: 'number' | 'text';
  amountText?: string;
  presetRate?: number;
  presetAmount?: number;
  // Which extraItems bucket this row lives in ("__root__", or a real row's
  // own key) — the Actions column's Edit/Delete/Add Item calls need this to
  // address the right bucket instead of assuming root.
  attachKey?: string;
};

const extraRowToStormRow = (row: BillSummaryExtraRow): ExtStormRow => ({
  key: row.key,
  rowType: row.nodeType === 'section' ? 'section' : 'item',
  reference: row.nodeType === 'section' ? undefined : row.description,
  description: row.nodeType === 'section' ? row.description : undefined,
  quantity: row.quantity,
  unit: row.unit,
  isExtra: true,
  nodeType: row.nodeType,
  amountType: row.amountType,
  amountText: row.amountText,
  presetRate: row.amountType === 'text' ? undefined : parseFloat(row.rate || '0') || undefined,
  presetAmount: row.amountType === 'text' ? undefined : parseFloat(row.amount || '0') || undefined,
  attachKey: row.attachKey,
  children: row.children?.map(extraRowToStormRow),
});

interface StormBuildUpBillSummaryProps {
  title: string;
  rows: StormBuildUpRowDef[];
  buildUp?: Record<string, FoulBuildUpRowData>;
  rateMap: Record<string, number>;
  /** Plain nett rate, no markup — only for the dev-mode "BC" modal, which
   * shows the backend's dbRate-only recomputation for reconciliation.
   * Marking it up too would make it trivially agree with the (now
   * marked-up) `rateMap`-derived Rate column and defeat its purpose as an
   * independent check. */
  nettRateMap: Record<string, number>;
  /** `(1 + OH&P%) × (1 + Preliminaries%)` — the same factor `rateMap` is
   *  already baked with, needed separately here so a newly-added ad-hoc extra
   *  item's own rate/amount marks up identically. */
  markupFactor: number;
  resolveEvaluateCode: (row: StormBuildUpRowDef) => string;
  showRateAmount?: boolean;
  /** Expand/collapse state lifted to the parent so its own Expand/Collapse All
   * button (next to the section Segmented control) can drive this table. */
  expandedRowKeys: React.Key[];
  onExpandedRowKeysChange: (keys: React.Key[]) => void;
  /** Dev-mode only: flattened persisted `roadAndSewers.summary.<domain>` map
   * (see flattenBoqSummaryLineItems), keyed the same as this table's rows, so
   * the SummaryKey column can show the last-saved amount next to the live one. */
  persistedItems?: Map<string, BoqSummaryLineItem>;
  /** Ad-hoc "Add Section"/"Add Item" extra rows for this specific build-up
   *  domain (e.g. `state.pondsSwalesExtraItems`/`state.cellularAttenuationExtraItems`
   *  — a separate bucket per instance, since this component is rendered once
   *  per build-up). Merged into this table's own rows here, but the CRUD hook
   *  itself (and the "Add Section" trigger) is owned by the parent
   *  (StormSewersBillSummary.tsx), so its own header can render "Add Section"
   *  right next to this section's Expand/Collapse All button — same
   *  convention PrelimsBillSummary.tsx uses for its single root table. */
  extraItems?: Record<string, DynamicBillSummaryNode[]>;
  extraItemsApi: ReturnType<typeof useBillSummaryExtraItems>;
  /** Whether the Actions column / inline-editable Qty & Unit cells / newly-added
   *  row autofocus should render — the parent's own `dynamicSummaryEnabled`. */
  dynamicSummaryEnabled: boolean;
  /** The parent's own newly-added-row id (for the brief highlight + Qty autofocus). */
  newlyAddedRowId: string | null;
}

const StormBuildUpBillSummary: React.FC<StormBuildUpBillSummaryProps> = ({
  title,
  rows,
  buildUp,
  rateMap,
  nettRateMap,
  markupFactor,
  resolveEvaluateCode,
  showRateAmount = true,
  expandedRowKeys,
  onExpandedRowKeysChange,
  persistedItems,
  extraItems,
  extraItemsApi,
  dynamicSummaryEnabled,
  newlyAddedRowId,
}) => {
  const { state } = useTender();
  const showDevMode = useDevMode();
  // SummaryKey modal — "BCCalculation" independent backend-dbRate-only recomputation,
  // same as groundworks Bill Summaries (BcCalculationDetails.tsx / computeBcCalculation).
  const [summaryKeyModalRecord, setSummaryKeyModalRecord] = useState<BoqSummaryLineItem | null>(null);
  const tableData: ExtStormRow[] = useMemo(() => {
    const realRows = buildStormBuildUpSummaryData(rows, buildUp, resolveEvaluateCode);
    // Graft ad-hoc extra rows onto this otherwise-computed tree — nested
    // additions merge onto the matching real pipe-type row (by `key`), root
    // additions become new top-level rows.
    const extraBuckets = extraItems ?? {};
    const injectExtraRows = (rowsIn: StormSummaryRow[]): ExtStormRow[] =>
      rowsIn.map((row) => {
        const bucket = extraBuckets[row.key];
        const ownChildren = row.children ? injectExtraRows(row.children) : undefined;
        if (!bucket || bucket.length === 0) {
          return ownChildren ? { ...row, children: ownChildren } : row;
        }
        const existingChildren = row.children ?? [];
        const extraRows = buildNestedExtraRows(bucket, row.key, row.key, existingChildren.length, markupFactor)
          .map(extraRowToStormRow);
        return { ...row, children: [...(ownChildren ?? []), ...extraRows] };
      });
    const injectedRealRows = injectExtraRows(realRows);
    const extraRootRows = buildRootExtraRows(extraBuckets['__root__'] ?? [], '__root__', 1, markupFactor)
      .map(extraRowToStormRow);
    return [...injectedRealRows, ...extraRootRows];
  }, [rows, buildUp, resolveEvaluateCode, extraItems, markupFactor]);

  const sumRows = (rowsToSum: ExtStormRow[]): number =>
    rowsToSum.reduce((acc, row) => {
      if (row.children) return acc + sumRows(row.children);
      if (row.rowType !== 'item') return acc;
      if (row.isExtra) return acc + (row.amountType === 'text' ? 0 : (row.presetAmount ?? 0));
      const qty = parseFloat(row.quantity ?? '');
      const rate = row.evaluateCode ? (getLibRate(rateMap, row.evaluateCode) ?? 0) : 0;
      return isNaN(qty) ? acc : acc + qty * rate;
    }, 0);

  const totalAmount = useMemo(() => sumRows(tableData), [tableData, rateMap]);

  const rowTotals = useMemo(() => {
    const map: Record<string, number> = {};
    tableData.forEach((row) => {
      if (row.children) map[row.key] = sumRows(row.children);
    });
    return map;
  }, [tableData, rateMap]);

  const columns = [
    // Every column renders independently for every row (no colSpan merge
    // trick) — same convention groundworks/GroundworksCutFillBillSummary.tsx
    // uses: a group row shows its title only in Description, blank in every
    // other column, Amount shows the collapsed subtotal. colSpan requires its
    // value to exactly match the live column count on every render
    // (Rate/Amount/SummaryKey/Actions all toggle independently here), and any
    // drift there visibly misaligns every column after it.
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (v: string, record: ExtStormRow) => {
        if (record.rowType === 'pipe-type') {
          return <Text strong className="text-indigo-600" style={{ textTransform: 'uppercase' }}>{record.reference}</Text>;
        }
        if (record.isExtra && record.nodeType === 'section') {
          return <Text strong className="text-indigo-600" style={{ textTransform: 'uppercase' }}>{record.description}</Text>;
        }
        if (record.isSubHeader) return <Text strong className="text-indigo-600" style={{ textTransform: 'uppercase' }}>{v}</Text>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Text>{record.isExtra ? record.reference : v}</Text>
            {showDevMode && !record.isExtra && record.evaluateCode && (
              <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2 }}>
                {record.evaluateCode}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right' as const,
      render: (v: string, record: ExtStormRow) => {
        if (record.rowType === 'pipe-type' || record.isSubHeader || (record.isExtra && record.nodeType === 'section')) {
          return '';
        }
        if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
          return (
            <NumberInput
              value={parseFloat(v) || 0}
              onChange={(qty) => extraItemsApi.updateNode(record.attachKey ?? '__root__', record.key as string, { qty })}
              size="small"
              decimalPlaces={2}
              autoFocus={record.key === newlyAddedRowId}
            />
          );
        }
        if (!v) return '';
        const n = parseFloat(v);
        if (!isNaN(n)) return <Text style={{ color: n < 0 ? '#cf1322' : undefined }}>{fmt(n)}</Text>;
        // Free-text quantity note (allowTextQuant rows, e.g. "900 x 675mm access opening").
        return <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text>;
      },
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 70,
      align: 'center' as const,
      render: (v: string, record: ExtStormRow) => {
        if (record.rowType === 'pipe-type' || record.isSubHeader || (record.isExtra && record.nodeType === 'section')) {
          return '';
        }
        if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
          return (
            <Select
              value={v || undefined}
              onChange={(unit) => extraItemsApi.updateNode(record.attachKey ?? '__root__', record.key as string, { unit })}
              options={extraItemsApi.unitOptions.map((u) => ({ value: u, label: u }))}
              placeholder="Unit"
              size="small"
              showSearch
              allowClear
              style={{ width: '100%' }}
            />
          );
        }
        return formatUnit(v) || '';
      },
    },
    ...(showRateAmount ? [{
      title: 'Rate (£)',
      key: 'rate',
      width: 110,
      align: 'right' as const,
      render: (_: unknown, record: ExtStormRow) => {
        if (record.rowType === 'pipe-type' || record.isSubHeader || (record.isExtra && record.nodeType === 'section')) {
          return '';
        }
        if (record.isExtra) return record.presetRate != null ? <Text>{fmt(record.presetRate)}</Text> : <Text type="secondary">—</Text>;
        if (!record.evaluateCode) return <Text type="secondary">—</Text>;
        const rate = getLibRate(rateMap, record.evaluateCode);
        return rate != null ? <Text>{fmt(rate)}</Text> : <Text type="secondary">—</Text>;
      },
    }] : []),
    ...(showRateAmount ? [{
      title: 'Amount (£)',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: ExtStormRow) => {
        if (record.rowType === 'pipe-type' || (record.isExtra && record.nodeType === 'section')) {
          const isCollapsed = !expandedRowKeys.includes(record.key);
          if (isCollapsed) {
            const subtotal = rowTotals[record.key] ?? 0;
            return subtotal > 0 ? <Text strong>{fmtCurrency(subtotal)}</Text> : null;
          }
          return '';
        }
        if (record.amountType === 'text') return <Text>{record.amountText}</Text>;
        if (record.isExtra) return record.presetAmount != null ? <Text>{fmt(record.presetAmount)}</Text> : <Text type="secondary">—</Text>;
        if (record.isSubHeader || !record.evaluateCode) return <Text type="secondary">—</Text>;
        const qty = parseFloat(record.quantity ?? '');
        const rate = getLibRate(rateMap, record.evaluateCode);
        if (isNaN(qty) || rate == null) return <Text type="secondary">—</Text>;
        return <Text>{fmt(qty * rate)}</Text>;
      },
    }] : []),
    // ── Dev-mode only: persisted-vs-live comparison column ───────────────────
    ...(showDevMode && showRateAmount && persistedItems ? [{
      title: 'SummaryKey',
      key: 'summaryKey',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: ExtStormRow) => {
        if (record.rowType === 'pipe-type' || record.isSubHeader || (record.isExtra && record.nodeType === 'section')) {
          return '';
        }
        if (record.isExtra) return <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>;
        const persisted = persistedItems.get(record.key);
        if (!persisted) return <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>;
        const qty = parseFloat(record.quantity ?? '');
        const rate = record.evaluateCode ? getLibRate(rateMap, record.evaluateCode) : undefined;
        const liveAmount = !isNaN(qty) && rate != null ? qty * rate : 0;
        const mismatch = Math.abs(liveAmount - (persisted.amount || 0)) > 0.01;
        const bcAmount = computeBcCalculation(persisted, (code) => nettRateMap[code] ?? 0).amount;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : '#6b7280' }}>
                {fmtCurrency(persisted.amount || 0)}
              </Text>
              <InfoCircleOutlined
                style={{ cursor: 'pointer', color: '#3b82f6' }}
                onClick={() => setSummaryKeyModalRecord(persisted)}
              />
            </div>
            <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
              BC: {fmtCurrency(bcAmount)}
            </Text>
          </div>
        );
      },
    }] : []),
    // ── Add Section/Add Item on any real or extra pipe-type row, Edit/Delete
    // on extra rows — a real pipe-type row is immutable itself but can still
    // host grafted extra rows. ────────────────────────────────────────────
    ...(dynamicSummaryEnabled && !state.isViewMode ? [{
      title: 'Actions',
      key: 'extraActions',
      width: 150,
      align: 'center' as const,
      render: (_: unknown, record: ExtStormRow) => {
        if (record.isExtra) {
          const bucketKey = record.attachKey ?? '__root__';
          const node = findExtraNode(extraItems?.[bucketKey] ?? [], record.key as string);
          if (!node) return '';
          if (record.nodeType === 'section') {
            return (
              <Space size={4}>
                <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(bucketKey, record.key as string)} />
                <Button size="small" icon={<EditOutlined />} title="Edit Section" onClick={() => extraItemsApi.openEditSection(bucketKey, node as DynamicBillSummarySectionNode)} />
                <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(bucketKey, record.key as string)} />
              </Space>
            );
          }
          return (
            <Space size={4}>
              <Button size="small" icon={<EditOutlined />} title="Edit Item" onClick={() => extraItemsApi.openEditItem(bucketKey, node as DynamicBillSummaryItemNode)} />
              <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(bucketKey, record.key as string)} />
            </Space>
          );
        }
        if (record.rowType === 'pipe-type') {
          return (
            <Space size={4}>
              <Button size="small" icon={<FolderAddOutlined />} title="Add Section" onClick={() => extraItemsApi.openAddSection(record.key as string, null)} />
              <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.key as string, null)} />
            </Space>
          );
        }
        return '';
      },
    }] : []),
  ];

  return (
    <>
    <Table<ExtStormRow>
      className="storm-bill-table"
      dataSource={tableData}
      columns={columns}
      rowKey="key"
      bordered
      size="middle"
      pagination={false}
      expandable={{
        childrenColumnName: 'children',
        expandedRowKeys,
        onExpand: (expanded, record) => {
          onExpandedRowKeysChange(
            expanded
              ? [...expandedRowKeys, record.key]
              : expandedRowKeys.filter((k) => k !== record.key),
          );
        },
        expandRowByClick: true,
      }}
      // 'pipe-type' rows here are this table's only group-header tier (its
      // Description column always renders them with the same text-indigo-600
      // used by StormSewersBillSummary.tsx's top-level 'section' rows, not its
      // 'pipe-type' sub-rows) — was 'bg-gray-50', mismatching that indigo text
      // and giving these build-up tabs a different collapsed-row background
      // than the main Storm Sewers Bill Summary tab.
      rowClassName={(record: ExtStormRow) => {
        const classes: string[] = [];
        if (record.rowType === 'pipe-type' || (record.isExtra && record.nodeType === 'section')) classes.push('bg-indigo-50', 'font-semibold');
        if (record.key === newlyAddedRowId) classes.push('storm-buildup-row-added');
        return classes.join(' ');
      }}
      summary={() => (
        <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 700 }}>
          <Table.Summary.Cell index={0} colSpan={showRateAmount ? 4 : 3}>
            <Text strong>TOTAL {title.toUpperCase()}</Text>
          </Table.Summary.Cell>
          {showRateAmount && (
            <Table.Summary.Cell index={1} align="right">
              <Text strong>{fmtCurrency(totalAmount)}</Text>
            </Table.Summary.Cell>
          )}
          {showDevMode && showRateAmount && persistedItems && <Table.Summary.Cell index={2} />}
          {dynamicSummaryEnabled && !state.isViewMode && <Table.Summary.Cell index={showDevMode && showRateAmount && persistedItems ? 3 : 2} />}
        </Table.Summary.Row>
      )}
    />
    <style>{`
      @keyframes storm-buildup-row-added-fade { 0% { background-color: #fef3c7; } 100% { background-color: transparent; } }
      .storm-buildup-row-added > td { animation: storm-buildup-row-added-fade 1.8s ease-out; }
    `}</style>
    <Modal
      title="SummaryKey — Amount Calculation"
      open={!!summaryKeyModalRecord}
      onCancel={() => setSummaryKeyModalRecord(null)}
      footer={null}
    >
      {summaryKeyModalRecord && (
        <BcCalculationDetails
          record={summaryKeyModalRecord}
          getDbRate={(code) => nettRateMap[code] ?? 0}
        />
      )}
    </Modal>
    {extraItemsApi.modals}
    </>
  );
};

export default StormBuildUpBillSummary;
